import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useBrandConfig } from '../context/BrandConfigContext';
import { useProductCache } from '../ui-components/context/ProductCache';
import { BrandIcon } from './BrandIcon';
import { formatPrice } from '../ui-components/types/product';
import type { Product } from '../ui-components/types/product';
import './ChatDemoAnimation.css';

// ============================================================
// CONVERSATION SCRIPT
// ============================================================

interface DemoMessage {
  role: 'user' | 'assistant';
  name: string;
  text: string;
  delayBefore: number;
  skus?: string[];
}

const CONVERSATION: DemoMessage[] = [
  {
    role: 'assistant',
    name: 'Closera AI',
    text: "Hi! I'm your personal stylist. What can I help you find today?",
    delayBefore: 800,
  },
  {
    role: 'user',
    name: 'Sarah',
    text: 'I need a date night outfit for this weekend, something classic and refined',
    delayBefore: 2000,
  },
  {
    role: 'assistant',
    name: 'Closera AI',
    text: "Great taste! Where are you headed? That'll help me nail the vibe.",
    delayBefore: 1600,
  },
  {
    role: 'user',
    name: 'Sarah',
    text: 'A nice Italian restaurant downtown',
    delayBefore: 1800,
  },
  {
    role: 'assistant',
    name: 'Closera AI',
    text: "Love that! Here are some stunning options for the evening:",
    delayBefore: 2000,
    skus: [
      'CO420_BL8133',  // Collection halter slip dress
      'CO015_BK0001',  // Collection pleated evening dress in taffeta
      'CP186_BK0001',  // Seamed sheath dress with velvet bows
      'CP527_RD0136',  // Collection scoopback mini dress in satin
    ],
  },
  {
    role: 'user',
    name: 'Sarah',
    text: 'I love the red one! Can you put together a full look around it?',
    delayBefore: 3000,
  },
  {
    role: 'assistant',
    name: 'Closera AI',
    text: "Absolutely! Here's a complete outfit:",
    delayBefore: 2000,
    skus: [
      'CP527_RD0136',  // Collection scoopback mini dress in satin (red)
      'CP058_BK0001',  // Maisie bow heels in patent leather
      'CP051_BK0001',  // Drawstring pouch bag
      'CO767_EC8333',  // Cupchain gemstone necklace
    ],
  },
];

// ============================================================
// TIMING
// ============================================================
const PAUSE_AT_END = 5500;
const RESTART_DELAY = 1200;
const TRACK_FADE_OUT_MS = 520;

// ============================================================
// MINI PRODUCT CAROUSEL
// ============================================================

function MiniCarousel({ skus, apiBaseUrl }: { skus: string[]; apiBaseUrl: string }) {
  const productCache = useProductCache();
  const [products, setProducts] = useState<(Product | null)[]>([]);
  const [focusIdx, setFocusIdx] = useState(() => Math.floor(skus.length / 2));

  useEffect(() => {
    let cancelled = false;
    productCache.getProducts(skus, apiBaseUrl).then(map => {
      if (cancelled) return;
      setProducts(skus.map(sku => map[sku] || null));
    });
    return () => { cancelled = true; };
  }, [skus, apiBaseUrl, productCache]);

  useEffect(() => {
    if (skus.length <= 1) return;
    const id = setInterval(() => {
      setFocusIdx(prev => (prev + 1) % skus.length);
    }, 2400);
    return () => clearInterval(id);
  }, [skus.length]);

  const loaded = products.filter(Boolean) as Product[];
  if (loaded.length === 0) {
    return (
      <div className="mini-carousel mini-carousel-loading">
        <div className="mini-carousel-loading-viewport" />
        <div className="mini-carousel-loading-caption" />
        <div className="mini-carousel-loading-dots" />
      </div>
    );
  }

  const current = loaded[focusIdx] || loaded[0];

  const count = skus.length;
  const goPrev = () => setFocusIdx((focusIdx - 1 + count) % count);
  const goNext = () => setFocusIdx((focusIdx + 1) % count);

  // Copied from Carousel3D.tsx getItemPosition, scaled 55% for mini size
  // Original: item 280×380, spacing 180px, perspective 1200px
  // Scaled:   item 154×209, spacing  99px, perspective  660px
  const S = 0.48;
  const positions: Record<number, { x: number; z: number; scale: number; opacity: number; blur: number }> = {
    0:    { x: 0,          z: 0,          scale: 1,    opacity: 1,   blur: 0 },
    1:    { x: 155 * S,    z: -80 * S,    scale: 0.85, opacity: 0.9, blur: 0.3 },
    [-1]: { x: -155 * S,   z: -80 * S,    scale: 0.85, opacity: 0.9, blur: 0.3 },
    2:    { x: 250 * S,    z: -200 * S,   scale: 0.75, opacity: 0.6, blur: 0.8 },
    [-2]: { x: -250 * S,   z: -200 * S,   scale: 0.75, opacity: 0.6, blur: 0.8 },
    3:    { x: 320 * S,    z: -300 * S,   scale: 0.6,  opacity: 0.3, blur: 1.5 },
    [-3]: { x: -320 * S,   z: -300 * S,   scale: 0.6,  opacity: 0.3, blur: 1.5 },
  };

  const getPositionProps = (pos: number) => {
    const clamped = Math.max(-3, Math.min(3, pos));
    const lower = Math.floor(clamped);
    const upper = Math.ceil(clamped);
    const t = clamped - lower;
    const lp = positions[lower] || positions[lower > 0 ? 3 : -3];
    const up = positions[upper] || positions[upper > 0 ? 3 : -3];
    return {
      x: lp.x + (up.x - lp.x) * t,
      z: lp.z + (up.z - lp.z) * t,
      scale: lp.scale + (up.scale - lp.scale) * t,
      opacity: lp.opacity + (up.opacity - lp.opacity) * t,
      blur: lp.blur + (up.blur - lp.blur) * t,
    };
  };

  // Identical to Carousel3D getItemPosition
  const getItemStyle = (index: number): React.CSSProperties => {
    let basePosition = (index - focusIdx + count) % count;
    if (basePosition > count / 2) basePosition -= count;
    const p = getPositionProps(basePosition);
    const isFront = Math.abs(basePosition) < 0.5;
    return {
      transform: `translateX(${p.x}px) translateZ(${p.z}px) scale(${p.scale})`,
      zIndex: Math.round(100 + p.z / 10),
      opacity: p.opacity,
      filter: p.blur > 0 ? `blur(${p.blur}px)` : 'none',
      pointerEvents: isFront ? 'auto' : 'none',
    };
  };

  return (
    <div className="mini-carousel">
      <div className="carousel-viewport-wrapper">
        <div className="carousel-viewport">
          {products.map((p, i) => p && (
            <div className="carousel-slide" key={skus[i]} style={getItemStyle(i)}>
              <div className="carousel-slide-inner">
                {(p.transparent_image_url || p.image_url) && (
                  <img src={p.transparent_image_url || p.image_url || ''} alt={p.name} className="carousel-slide-img" />
                )}
              </div>
            </div>
          ))}
        </div>
        {skus.length > 1 && (
          <>
            <div className="carousel-nav-area carousel-nav-left" onClick={goPrev}>
              <svg className="carousel-nav-arrow" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </div>
            <div className="carousel-nav-area carousel-nav-right" onClick={goNext}>
              <svg className="carousel-nav-arrow" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </div>
          </>
        )}
      </div>
      <div className="carousel-caption">
        <span className="carousel-caption-name">{current.name}</span>
        {current.price != null && (
          <span className="carousel-caption-price">{formatPrice(current.price)}</span>
        )}
      </div>
      {skus.length > 1 && (
        <div className="carousel-dots">
          {skus.map((_, i) => (
            <span key={i} className={`carousel-dot ${i === focusIdx ? 'active' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

interface ChatDemoAnimationProps {
  apiBaseUrl?: string;
}

export function ChatDemoAnimation({ apiBaseUrl = '' }: ChatDemoAnimationProps) {
  const params = new URLSearchParams(window.location.search);
  const mobile = params.get('mobile') === 'true';
  const scrollStepDuration = mobile ? 680 : 760;
  // Mobile anchors the first message near the top of the stage with a small
  // padding for breathing room. Desktop keeps a large spacer so the first
  // message starts centered and scrolls up.
  const spacerRatio = mobile ? 0 : 0.58;
  const mobileSpacerPaddingPx = 10;

  const { fonts } = useBrandConfig();
  const productCache = useProductCache();
  const [feedMessageIndexes, setFeedMessageIndexes] = useState<number[]>([]);
  const [trackFadingOut, setTrackFadingOut] = useState(false);
  const [topSpacerPx, setTopSpacerPx] = useState(mobile ? mobileSpacerPaddingPx : 278);
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);

  const preloadSkus = useMemo(
    () => Array.from(new Set(CONVERSATION.flatMap(msg => msg.skus || []))),
    []
  );

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const sleep = useCallback((ms: number) => {
    return new Promise<void>(resolve => {
      const id = setTimeout(resolve, ms);
      timeoutsRef.current.push(id);
    });
  }, []);

  const nextFrame = useCallback(() => {
    return new Promise<void>(resolve => {
      requestAnimationFrame(() => resolve());
    });
  }, []);

  const cancelScrollAnimation = useCallback(() => {
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  const animateScrollToBottom = useCallback((durationMs = 760) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const target = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
    const start = viewport.scrollTop;
    const delta = target - start;

    if (durationMs <= 0 || Math.abs(delta) < 1) {
      viewport.scrollTop = target;
      return;
    }

    cancelScrollAnimation();

    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const progress = Math.min(1, (now - startTime) / durationMs);
      viewport.scrollTop = start + delta * easeOutCubic(progress);

      if (progress < 1) {
        scrollRafRef.current = requestAnimationFrame(tick);
      } else {
        scrollRafRef.current = null;
      }
    };

    scrollRafRef.current = requestAnimationFrame(tick);
  }, [cancelScrollAnimation]);

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setTrackFadingOut(false);
      setFeedMessageIndexes([0]);
      await nextFrame();
      animateScrollToBottom(0);

      for (let i = 1; i < CONVERSATION.length; i++) {
        if (!loopRef.current) return;
        await sleep(CONVERSATION[i].delayBefore);
        setFeedMessageIndexes(prev => [...prev, i]);
        await nextFrame();
        animateScrollToBottom(scrollStepDuration);
      }

      await sleep(PAUSE_AT_END);
      setTrackFadingOut(true);
      await sleep(TRACK_FADE_OUT_MS);
      cancelScrollAnimation();
      setFeedMessageIndexes([]);
      await nextFrame();
      animateScrollToBottom(0);
      await sleep(RESTART_DELAY);
    }
  }, [sleep, nextFrame, animateScrollToBottom, cancelScrollAnimation, scrollStepDuration]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      clearAllTimeouts();
      cancelScrollAnimation();
    };
  }, [runLoop, clearAllTimeouts, cancelScrollAnimation]);

  useEffect(() => {
    if (!apiBaseUrl || preloadSkus.length === 0) return;
    productCache.getProducts(preloadSkus, apiBaseUrl).catch(() => {
      // Best-effort preload for smoother carousel reveal.
    });
  }, [apiBaseUrl, preloadSkus, productCache]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (feedMessageIndexes.length === 0) return;
      animateScrollToBottom(460);
    });

    observer.observe(track);
    return () => observer.disconnect();
  }, [feedMessageIndexes.length, animateScrollToBottom]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const updateSpacer = () => {
      const ratioPx = Math.round(viewport.clientHeight * spacerRatio);
      setTopSpacerPx(mobile ? mobileSpacerPaddingPx : ratioPx);
    };

    updateSpacer();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateSpacer());
      observer.observe(viewport);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateSpacer);
    return () => window.removeEventListener('resize', updateSpacer);
  }, [spacerRatio, mobile, mobileSpacerPaddingPx]);

  const bg = params.get('bg') || 'demo';
  const fullpage = params.get('fullpage') !== 'false';
  const showGuide = params.get('guide') === 'true';
  const embed = params.get('embed') === 'true';

  const rootClasses = [
    'demo-root',
    fullpage && !embed ? 'fullpage' : '',
    embed ? 'embed' : '',
    mobile ? 'demo-mobile' : '',
    bg === 'demo' ? 'bg-demo' : '',
    bg === 'white' ? 'bg-white' : '',
    bg === 'green' ? 'bg-green' : '',
  ].filter(Boolean).join(' ');

  const messages = feedMessageIndexes.map(index => CONVERSATION[index]).filter(Boolean);

  return (
    <div className={rootClasses}>
      {showGuide && <div className="demo-rec-guide" />}
      <div className="demo-stage" ref={viewportRef}>
        <div
          className={`demo-scroll-track${trackFadingOut ? ' fading-out' : ''}`}
          ref={trackRef}
        >
          <div className="demo-scroll-top-spacer" style={{ height: `${topSpacerPx}px` }} />
          {messages.map((msg, i) => (
            <div
              key={`${msg.role}-${msg.text}-${i}`}
              className={`demo-msg demo-msg-${msg.role}`}
            >
              <div className="demo-bubble">
                <div className="demo-name">
                  <span className="demo-name-icon" style={{ fontFamily: msg.role === 'assistant' ? fonts.brandFont : undefined }}>
                    {msg.role === 'assistant' ? <BrandIcon size={12} /> : msg.name[0]}
                  </span>
                  {msg.name}
                </div>
                <div className="demo-bubble-text">{msg.text}</div>
              </div>

              {msg.skus && msg.skus.length > 0 && (
                <div className="demo-rich-content">
                  <MiniCarousel skus={msg.skus} apiBaseUrl={apiBaseUrl} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
